import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import {
  ThemeProvider, Typography, CircularProgress, Link, Box,
} from '@material-ui/core';

import { useLazyQuery } from '@apollo/client';

import { useActivation } from '../ActivationContext';
import { useActivationBase } from '../hooks/useActivationBase';
import usePostAuthNavigation from '../hooks/usePostAuthNavigation';
import {
  AccessibleButton,
  ContentContainer,
  FlexContainer,
  HeaderBox,
  Title,
  continueButtonStyles,
} from './AuthMethodSelection.styles';

import sleepioTheme from '@/themes/sleepioTheme';
import { TextField } from '@/components';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { SleepioHeader } from '@/pages/Onboarding/components/Header/SleepioHeader';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { useConsent } from '@/hooks/useConsent';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { isActivatedMember } from '@/utils/entitlements';
import { loginUser } from '@/utils/sparkActivationApi';
import { logError } from '@/utils/logError';
import { buildRedirectParam } from '@/utils/urlHelpers';
import { LOGIN_ORG_SLUG_PLACEHOLDER } from '@/utils/constants';
import { FORGOT_PASSWORD_PATH } from '@/utils/passwordRoutes';
import { QUERY_DISTINCT_ID_WITH_EMAIL } from '@/apollo/queries';
import { linkPostHogIdentity, SLEEPIO_CANOPY_PROGRAM } from '@/utils/posthog';
import {
  setPreferredSignInMethod,
  trackSignInAbandoned,
  trackSignInFailed,
  trackSignInMethodSelected,
  trackSignInSubmitted,
  trackSignInSucceeded,
  type SignInFailureReason,
} from '@/utils/signInAnalytics';

/**
 * "Enter your password" — sign-in, and the one screen shown when it doesn't work.
 *
 * **The failure state must look the same whatever caused it.** A wrong password and
 * an address that has never had a password set have to produce byte-identical output,
 * because telling them apart would reveal whether an address belongs to a member.
 * The screen gets that property structurally rather than by careful wording: the
 * outcome below is a three-value flag with no room for a cause, and the error the
 * platform returned is logged but never stored, never rendered, and never branched on.
 * If a future change adds a "reason" to that state, it breaks a security property, not
 * a copy detail.
 *
 * **There is no password rule on this screen, deliberately.** The twelve-character
 * rule belongs on the screen where a member *chooses* a password. Enforcing it here
 * would lock out anyone whose existing password predates the rule, and would tell a
 * stranger what the rule is.
 */

type Outcome = 'idle' | 'submitting' | 'failed' | 'sending_link';

const PasswordEntryInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();

  const { t } = useTranslation();
  const history = useHistory();
  const base = useActivationBase();
  // eslint-disable-next-line camelcase
  const { lob, org_slug: orgSlug } = useParams<{ lob: string; org_slug: string }>();
  const { state, dispatch } = useActivation();
  const navigatePostAuth = usePostAuthNavigation();
  const posthog = usePostHog();
  const [getDistinctId] = useLazyQuery(QUERY_DISTINCT_ID_WITH_EMAIL, { fetchPolicy: 'network-only' });

  const [password, setPassword] = useState('');
  const [outcome, setOutcome] = useState<Outcome>('idle');
  // Read on the way out, so it must not be state — a re-render must not reset it and
  // the cleanup below has to see the latest value, not the one captured at mount.
  const resolved = useRef(false);

  const { email } = state;
  const redirectParam = buildRedirectParam(state.redirectTo);

  useEffect(() => {
    dispatch({ type: 'SET_STEP', step: 'email_password' });
    dispatch({ type: 'SET_AUTH_METHOD', authMethod: 'email_password' });
  }, [dispatch]);

  // Left the sign-in screen with nothing resolved — no session, and no switch to the
  // emailed link either. Moving to the link is a continued sign-in rather than an
  // abandoned one, so it counts as resolved here and is measured by its own event.
  //
  // The client is held in a ref and the dependency list is empty, so this runs once,
  // when the screen actually goes away. Depending on the client object directly would
  // re-run the effect — and fire its cleanup — on every render that handed back a
  // different instance, reporting an abandonment each time the member typed a
  // character.
  const posthogRef = useRef(posthog);
  // Assigned in an effect, not during render: React treats the render phase as
  // side-effect free, and a stray write there misbehaves under concurrent rendering.
  useEffect(() => { posthogRef.current = posthog; });
  useEffect(() => () => {
    if (!resolved.current) {
      trackSignInAbandoned(posthogRef.current);
    }
  }, []);

  useEffect(() => {
    if (!email) {
      // Not an abandonment: this member is being bounced back to collect an address
      // and never saw the screen. Marking it resolved keeps them out of the count.
      resolved.current = true;
      history.replace(`${base}/email${redirectParam}`);
    }
  }, [email, history, base, redirectParam]);

  if (!email) { return null; }

  const handleSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!password || outcome === 'submitting') { return; }

    setOutcome('submitting');
    trackSignInSubmitted(posthog);

    let session;
    try {
      const { data, error } = await getSupabaseClient().auth.signInWithPassword({ email, password });

      if (error || !data?.session) {
        // Logged, never surfaced. Supabase returns the same error for a wrong password
        // and for an account with no password, and even if it stopped doing so, nothing
        // here would pass the difference on to the screen.
        logError('[PasswordEntry]', 'signInWithPassword rejected', error ?? undefined);
        // The analytics reason is as blunt as the screen: one value covers both a
        // wrong password and an account that never had one, because the two cannot be
        // told apart here. Nothing measures the size of that second group, and that is
        // an accepted trade — a reason that separated them would disclose exactly what
        // the shared screen exists to hide.
        const reason: SignInFailureReason = error?.status === 429 ? 'rate_limited' : 'wrong_credentials';
        trackSignInFailed(posthog, reason);
        setOutcome('failed');
        return;
      }
      session = data.session;
    } catch (err) {
      logError('[PasswordEntry]', 'signInWithPassword threw', err);
      trackSignInFailed(posthog, 'unknown');
      setOutcome('failed');
      return;
    }

    resolved.current = true;
    trackSignInSucceeded(posthog);
    setPreferredSignInMethod(posthog, 'password');

    dispatch({ type: 'SET_SESSION', session });
    linkPostHogIdentity(getDistinctId, posthog, email, '[PasswordEntry]');

    // Route the member who never finished activating, rather than refusing them.
    // This is the same branch the emailed link already takes: read the entitlement
    // status out of the session token — no network call, the answer is already in the
    // token they are holding — and send anyone who is not active back into activation
    // to finish it. There is no error screen here on purpose.
    if (!isActivatedMember(session, 'sleepio')) {
      // Send them down the *account-creation* route, not this one.
      //
      // Two things go wrong otherwise, both silently. The coverage check reads the
      // organisation out of the address, and this route carries a stand-in rather than
      // a real one — so eligibility would be decided, and the member's details saved,
      // against an organisation that does not exist. And the final step only enrols a
      // member in the clinical programme when it believes it is creating an account,
      // so staying on this route would hand them off having never been enrolled.
      //
      // The emailed link has always done exactly this: it rewrites both the
      // organisation and the intent before sending an unactivated member into
      // activation. Matching it is the whole requirement.
      const canActivateHere = orgSlug && orgSlug !== LOGIN_ORG_SLUG_PLACEHOLDER;

      if (!canActivateHere) {
        // No real organisation to check coverage against. Rather than submit an
        // eligibility check against a stand-in, send them to choose their employer —
        // which is where an activation with no organisation is supposed to begin.
        logError('[PasswordEntry]', 'no organisation on the sign-in route; cannot start activation here', undefined, {
          level: 'warning',
        });
        history.push(`/activate/sleepio-canopy/${lob}`);
        return;
      }

      dispatch({ type: 'SET_STEP', step: 'coverage_form' });
      history.push(`/activate/sleepio-canopy/${lob}/${orgSlug}/sign-up/coverage${redirectParam}`);
      return;
    }

    navigatePostAuth();
  };

  const handleEmailLink = async () => {
    setOutcome('sending_link');
    trackSignInMethodSelected(posthog, 'magic_link');
    try {
      await loginUser({
        email,
        product: 'sleepio',
        ph_distinct_id: posthog?.get_distinct_id() || undefined,
      });
      linkPostHogIdentity(getDistinctId, posthog, email, '[PasswordEntry]');
      // Only now: the link is genuinely on its way, so this is a sign-in that
      // continued rather than one that was given up on. Setting it before the call
      // would have hidden the abandonment that follows a failed send — the branch
      // where leaving is most likely.
      resolved.current = true;
      dispatch({ type: 'SET_AUTH_METHOD', authMethod: 'email' });
      dispatch({ type: 'SET_STEP', step: 'magic_link_sent' });
      history.push(`${base}/sent${redirectParam}`);
    } catch (err) {
      logError('[PasswordEntry]', 'loginUser failed', err);
      posthog?.capture('ActivationFlowErrored', { program: SLEEPIO_CANOPY_PROGRAM });
      setOutcome('failed');
    }
  };

  // `sending_link` is included so the view does not flip back to the form mid-send.
  // It is the failure *view*, not a failure state.
  const showFailureView = outcome === 'failed' || outcome === 'sending_link';

  return (
    <ContentContainer>
      <SleepioHeader />
      <FlexContainer maxWidth="xs">
        <HeaderBox style={{ marginBottom: 24 }}>
          <Title variant="h2" color="textPrimary">
            {t('coverage.pre_onboarding.sleepio.activation.password_entry.title')}
          </Title>
          <Typography color="textPrimary">
            {t('coverage.pre_onboarding.sleepio.activation.password_entry.sub_title', { email })}
          </Typography>
        </HeaderBox>

        <form onSubmit={handleSignIn}>
          <TextField
            id="password"
            label={t('coverage.pre_onboarding.sleepio.activation.password_entry.password_label')}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={showFailureView}
            helperText={showFailureView
              ? t('coverage.pre_onboarding.sleepio.activation.password_entry.failed_helper')
              : undefined}
            inputProps={{
              type: 'password',
              autoCapitalize: 'none',
              autoCorrect: 'off',
              autoComplete: 'current-password',
              'data-testid': 'password-input',
            }}
            required
          />

          {showFailureView && (
            <Box data-testid="try-a-link-notice" style={{ marginTop: 16, marginBottom: 16 }}>
              <Typography color="textPrimary" style={{ fontWeight: 700 }}>
                {t('coverage.pre_onboarding.sleepio.activation.password_entry.failed_notice_title')}
              </Typography>
              <Typography color="textPrimary">
                {t('coverage.pre_onboarding.sleepio.activation.password_entry.failed_notice_body')}
              </Typography>
            </Box>
          )}

          {showFailureView ? (
            <>
              <AccessibleButton
                variant="contained"
                data-testid="email-link-button"
                type="button"
                fullWidth
                color="primary"
                rounded
                disabled={outcome === 'sending_link'}
                style={continueButtonStyles}
                onClick={handleEmailLink}
              >
                {outcome === 'sending_link' ? (
                  <>
                    <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />
                    {t('coverage.pre_onboarding.sleepio.activation.sending')}
                  </>
                ) : t('coverage.pre_onboarding.sleepio.activation.password_entry.failed_email_link')}
              </AccessibleButton>

              <Typography align="center">
                <Link
                  component="button"
                  type="button"
                  data-testid="retry-password-link"
                  onClick={() => { setPassword(''); setOutcome('idle'); }}
                >
                  {t('coverage.pre_onboarding.sleepio.activation.password_entry.failed_retry')}
                </Link>
              </Typography>
            </>
          ) : (
            <>
              <AccessibleButton
                variant="contained"
                data-testid="submit-password-button"
                type="submit"
                fullWidth
                color="primary"
                rounded
                disabled={!password || outcome === 'submitting'}
                style={continueButtonStyles}
              >
                {outcome === 'submitting' ? (
                  <>
                    <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />
                    {t('coverage.pre_onboarding.sleepio.activation.password_entry.submitting')}
                  </>
                ) : t('coverage.pre_onboarding.sleepio.activation.password_entry.submit')}
              </AccessibleButton>

              <Typography align="center">
                <Link
                  component="button"
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => history.push({ pathname: FORGOT_PASSWORD_PATH, state: { email } })}
                >
                  {t('coverage.pre_onboarding.sleepio.activation.password_entry.forgot_password')}
                </Link>
              </Typography>
            </>
          )}
        </form>

        <ConsentWidget />
      </FlexContainer>
      <Footer />
    </ContentContainer>
  );
};

export const PasswordEntry: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <PasswordEntryInner />
  </ThemeProvider>
);

export default PasswordEntry;
